import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TrackingClient } from '@sparefoot/tracking-client';
import './YoutubeVideo.scss';

let youtubePlayerCounter = 0;

const YoutubeVideo = ({ src, videoName }) => {
	const iframeRef = useRef(null);
	const playerIdRef = useRef(`youtube-player-${++youtubePlayerCounter}`);
	const [videoDuration, setVideoDuration] = useState(0);
	const [playedTime, setPlayedTime] = useState(0);
	const [hasSentEvent, setHasSentEvent] = useState(false);
	const [player, setPlayer] = useState(null);
	const [intervalId, setIntervalId] = useState(null);

	const hasSentEventRef = useRef(hasSentEvent);
	const playerRef = useRef(player);
	const intervalRef = useRef(intervalId);
	const playedTimeRef = useRef(playedTime);
	const videoDurationRef = useRef(videoDuration);

	hasSentEventRef.current = hasSentEvent;
	playerRef.current = player;
	intervalRef.current = intervalId;
	playedTimeRef.current = playedTime;
	videoDurationRef.current = videoDuration;

	const trackVideoEvent = useCallback(() => {
		setHasSentEvent(true);
		TrackingClient.trackVideoWatched({
			video_duration: videoDurationRef.current,
			video_duration_played: playedTimeRef.current,
			video_name: videoName,
		});
	}, [videoName]);

	const onStateChange = useCallback(
		(event) => {
			// This method is called anytime the video state changes
			// playing, paused, ended, buffering, unstarted
			const playerState = globalThis.YT.PlayerState;
			if (event.data === playerState.PLAYING && !intervalRef.current) {
				const newInterval = setInterval(() => {
					if (playerRef.current) {
						setPlayedTime(playerRef.current.getCurrentTime());
					}
				}, 1000);
				setIntervalId(newInterval);
			} else if (
				playerRef.current?.getCurrentTime() > 10 &&
				!hasSentEventRef.current
			) {
				// If the video has played more than 10 seconds, and we haven't sent a tracking evnet yet
				// Keep in mind this only fires on state changes, so when the video ends, pauses, etc
				trackVideoEvent();
				clearInterval(intervalRef.current);
				setIntervalId(null);
			} else {
				clearInterval(intervalRef.current);
				setIntervalId(null);
			}
		},
		[trackVideoEvent],
	);

	const onYouTubeIframeAPIReady = useCallback(() => {
		// If the iframe doesn't exist, or the player isn't created return
		if (!iframeRef.current || !globalThis.YT) return;

		// Binds iframeRef to the iframe and manages player control assignment
		iframeRef.current.id = playerIdRef.current;
		const newPlayer = new globalThis.YT.Player(iframeRef.current.id, {
			events: {
				onReady: (event) => {
					setVideoDuration(event.target.getDuration()); // When the video loads, set the total video duration for tracking event
				},
				onStateChange, // Use the explicitly bound function
			},
		});
		setPlayer(newPlayer);
	}, [onStateChange]);

	// Create a script tag to use the Youtube Iframe Player API/make the iframe a controlled component
	useEffect(() => {
		if (globalThis.YT) {
			onYouTubeIframeAPIReady();
		} else {
			const tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';
			document.body.appendChild(tag);
			globalThis.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Verifies interval stops firing on navigation
	useEffect(() => {
		return () => {
			clearInterval(intervalRef.current);
		};
	}, []);

	return (
		<div className="ph-responsive-embed">
			<iframe
				title={videoName || 'video'}
				src={src}
				ref={iframeRef}
				allowFullScreen
				referrerPolicy="strict-origin-when-cross-origin"
			/>
		</div>
	);
};

YoutubeVideo.propTypes = {
	src: PropTypes.string.isRequired,
	videoName: PropTypes.string.isRequired,
};

export default YoutubeVideo;
